import { useAuth } from '@/auth/useAuth';
import { useCorreccionesPendientes } from '@/hooks/useCorreccionesPendientes';
import { Bell, ChevronDownIcon, LogOut, PhoneIcon, RotateCw, Shield } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ImpersonationBanner } from '../ImpersonationBanner';

export function Header() {
  const { logout, user, switchToAdmin, isImpersonating } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAsesor = user?.asesor?.rol === 'ASESOR';
  const isSysadmin = user?.asesor?.rol === 'SYSADMIN';
  const [isOpen, setIsOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const {
    empresas: empresasConCorrecciones,
    count: correccionesCount,
    refetch: refetchCorrecciones,
    isLoading: isNotifLoading,
  } = useCorreccionesPendientes();

  useEffect(() => {
    document.documentElement.style.setProperty('--header-height', '64.5px');

    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setIsNotifOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setIsNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleCorreccionClick = (nit: string, asesoriaId: string, estado?: string | null) => {
    setIsNotifOpen(false);
    const query = estado === 'REQUIERE_CORRECCIONES' ? '?tab=correcciones' : '';
    navigate(`/empresa/${nit}/asesoria/${asesoriaId}${query}`);
  };

  const isOnAsesoriaView = /^(?:\/empresa\/[^/]+\/asesoria(?:\/.*)?)$/.test(location.pathname);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap');
      `}</style>
      <header
        className="sticky z-50"
        style={{
          top: '0',
          background: '#fff',
          borderBottom: '0.5px solid #e8e4e0',
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <ImpersonationBanner />
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
          {/* Left — logo + nav */}
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="hover:opacity-75 transition-opacity rounded-lg shadow-sky-600 drop-shadow-zinc-500 bg-[#f4f1f2] px-3 py-1"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {isOnAsesoriaView ? 'Volver al menú principal' : 'SIG · Turismo de Aventura'}
            </Link>

            {/* Subtle divider */}
            {isAsesor && <div style={{ width: '0.5px', height: 18, background: '#e8e4e0' }} />}
          </div>

          {/* Right — notifications + user menu */}
          <div className="flex items-center gap-2">
            {isAsesor && (
              <div className="relative" ref={notifRef}>
                <button
                  type="button"
                  onClick={() => setIsNotifOpen((open) => !open)}
                  className="relative flex items-center justify-center w-9 h-9 rounded-full transition-colors"
                  style={{ background: isNotifOpen ? '#f4f1ed' : 'transparent' }}
                  onMouseEnter={(e) => {
                    if (!isNotifOpen) e.currentTarget.style.background = '#f4f1ed';
                  }}
                  onMouseLeave={(e) => {
                    if (!isNotifOpen) e.currentTarget.style.background = 'transparent';
                  }}
                  aria-label="Correcciones pendientes"
                >
                  <Bell className="w-4.5 h-4.5" style={{ color: '#6b5f55' }} />
                  {correccionesCount > 0 && (
                    <span
                      className="absolute top-0.5 right-0.5 flex items-center justify-center rounded-full text-white text-[10px] font-medium"
                      style={{ minWidth: 16, height: 16, padding: '0 3px', background: '#c0392b' }}
                    >
                      {correccionesCount}
                    </span>
                  )}
                </button>

                {isNotifOpen && (
                  <div
                    className="absolute right-0 mt-2 w-80 overflow-hidden"
                    style={{
                      background: '#fff',
                      border: '0.5px solid #e8e4e0',
                      borderRadius: 12,
                      boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                    }}
                  >
                    <div
                      className="px-4 py-3 flex items-center justify-between"
                      style={{ borderBottom: '0.5px solid #e8e4e0', background: '#faf8f6' }}
                    >
                      <p className="text-sm" style={{ color: '#1a1816', fontWeight: 500 }}>
                        Correcciones pendientes
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          refetchCorrecciones();
                        }}
                        disabled={isNotifLoading}
                        className={`p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors ${
                          isNotifLoading ? 'animate-spin' : ''
                        }`}
                        aria-label="Actualizar notificaciones"
                      >
                        <RotateCw className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {empresasConCorrecciones.length === 0 ? (
                      <div className="px-4 py-6 text-sm text-center" style={{ color: '#a09488' }}>
                        No tienes correcciones pendientes.
                      </div>
                    ) : (
                      <div className="max-h-80 overflow-y-auto">
                        {empresasConCorrecciones.map((empresa) => (
                          <button
                            key={empresa.nit}
                            type="button"
                            onClick={() => handleCorreccionClick(empresa.nit, empresa.asesoriaActivaId ?? '', empresa.asesoriaActivaEstado)}
                            className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-[#faf8f6]"
                            style={{ borderBottom: '0.5px solid #f0ece8' }}
                          >
                            <span className="text-sm truncate" style={{ color: '#1a1816' }}>
                              {empresa.nombre}
                            </span>
                            {!!empresa.cantidadAsesoriasConCorreccionesPendientes && (
                              <span
                                className="shrink-0 rounded-full text-white text-[11px] font-medium px-2 py-0.5"
                                style={{ background: '#c0392b' }}
                              >
                                {empresa.cantidadAsesoriasConCorreccionesPendientes === 1
                                  ? '1 asesoría con corrección'
                                  : `${empresa.cantidadAsesoriasConCorreccionesPendientes} asesorías con corrección`}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="relative" ref={dropdownRef}>
              {user && (
                <>
                  <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center gap-2 rounded-full transition-colors"
                    style={{ padding: '4px 10px 4px 4px', background: isOpen ? '#f4f1ed' : 'transparent' }}
                    onMouseEnter={(e) => {
                      if (!isOpen) e.currentTarget.style.background = '#f4f1ed';
                    }}
                    onMouseLeave={(e) => {
                      if (!isOpen) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <img
                      src={user.picture}
                      alt={user.asesor?.nombre ?? user.name}
                      className="w-8 h-8 rounded-full"
                      style={{ border: '0.5px solid #e8e4e0' }}
                    />
                    <span className="hidden sm:block text-sm max-w-30 truncate" style={{ color: '#6b5f55', fontWeight: 400 }}>
                      {user.asesor?.nombre?.split(' ')[0] ?? user.name?.split(' ')[0]}
                    </span>
                    <ChevronDownIcon
                      className={`w-4 h-4 hidden sm:block transition-transform`}
                      style={{ color: '#b0a898', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    />
                  </button>

                  {isOpen && (
                    <div
                      className="absolute right-0 mt-2 w-72 overflow-hidden"
                      style={{
                        background: '#fff',
                        border: '0.5px solid #e8e4e0',
                        borderRadius: 12,
                        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                      }}
                    >
                      {/* Profile header */}
                      <div className="p-4" style={{ borderBottom: '0.5px solid #e8e4e0', background: '#faf8f6' }}>
                        <div className="flex items-center gap-3">
                          <img
                            src={user.picture}
                            alt={user.asesor?.nombre ?? user.name}
                            className="w-12 h-12 rounded-full"
                            style={{ border: '0.5px solid #e8e4e0' }}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm truncate" style={{ color: '#1a1816', fontWeight: 500 }}>
                              {user.asesor?.nombre ?? user.name}
                            </p>
                            <p className="text-xs truncate mt-0.5" style={{ color: '#a09488' }}>
                              {user.asesor?.correo ?? user.email}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Phone */}
                      {user.asesor?.telefono && (
                        <div
                          className="px-4 py-3 flex items-center gap-3 text-sm"
                          style={{ color: '#a09488', borderBottom: '0.5px solid #f0ece8' }}
                        >
                          <PhoneIcon className="w-3.5 h-3.5 shrink-0" style={{ color: '#c5bdb5' }} />
                          {user.asesor.telefono}
                        </div>
                      )}

                      {/* Logout */}
                      <div className="p-2 space-y-1">
                        {isSysadmin && !isImpersonating && (
                          <button
                            type="button"
                            onClick={() => {
                              switchToAdmin();
                              setIsOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors text-blue-600 bg-transparent hover:bg-blue-50"
                          >
                            <Shield className="w-4 h-4" />
                            Cambiar a ADMIN
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={logout}
                          className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors"
                          style={{ color: '#c0392b', background: 'transparent' }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#fdf2f2')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <LogOut className="w-4 h-4" />
                          {isImpersonating ? 'Volver a mi sesión' : 'Cerrar sesión'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
